import {
  Button,
  CloseButton,
  Grid,
  Heading,
  IconInfoSolid,
  Modal,
  Spinner,
  TextInput,
  Tooltip,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { AssignmentTypeService } from "../../services/AssignmentTypeService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";

export const AssignmentTypeEditModal = ({
  open,
  assignmentType,
  onCloseHandler,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [newAssignmentType, setNewAssignmentType] = useState({
    ...assignmentType,
  });
  const [loading, isLoading] = useState(false);

  const handleClose = (updated) => {
    open = false;
    onCloseHandler(updated);
  };

  const handleSubmit = () => {
    isLoading(true);
    const promise = newAssignmentType.id
      ? AssignmentTypeService.updateAssignmentType(newAssignmentType)
      : AssignmentTypeService.createAssignmentType(newAssignmentType);
    promise
      .then(() => {
        isLoading(false);
        handleClose(true);
        showNotification(t("notifications.assignmentType.updateSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.assignmentType.updateError")} ${e}`,
          ERROR
        );
      });
  };

  const getModalHeader = () => (
    <Modal.Header>
      <Grid hAlign="space-between">
        <Grid.Row>
          <Grid.Col>
            <Heading level="h2" as="h1">
              {t("assignment.configure")}
              <Tooltip
                renderTip={t("assignment.example")}
                placement="top"
                on={["click", "hover", "focus"]}
                color="primary-inverse"
              >
                <IconInfoSolid size="x-small" color="brand" />
              </Tooltip>
            </Heading>
          </Grid.Col>
          <Grid.Col width="auto">
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={handleClose}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Modal.Header>
  );

  const getModalBody = () => {
    return (
      <>
        <Grid colSpacing="small" rowSpacing="small" vAlign="middle">
          <Grid.Row key={"edited-assignment-row"}>
            <Grid.Col width={4}>
              <TextInput
                value={newAssignmentType.name ? newAssignmentType.name : ""}
                interaction={"enabled"}
                id={"att-type-name"}
                renderLabel={t("attainmentLevel.name")}
                onChange={(e) => {
                  newAssignmentType.name = e.currentTarget.value;
                  setNewAssignmentType({ ...newAssignmentType });
                }}
              />
            </Grid.Col>
            <Grid.Col width={8}>
              <TextInput
                value={
                  newAssignmentType.description
                    ? newAssignmentType.description
                    : ""
                }
                interaction={"enabled"}
                renderLabel={t("attainmentLevel.description")}
                onChange={(e) => {
                  newAssignmentType.description = e.currentTarget.value;
                  setNewAssignmentType({ ...newAssignmentType });
                }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </>
    );
  };

  const getModalFooter = () => (
    <Modal.Footer>
      <Button margin="0 small 0 0" onClick={handleClose}>
        {t("global.cancel")}
      </Button>

      <Button
        color="primary"
        type="submit"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
        ) : (
          t("global.submit")
        )}
      </Button>
    </Modal.Footer>
  );

  return (
    <Modal
      key={"assignment-type-edit-modal"}
      open={open}
      onDismiss={handleClose}
      size="large"
      label={t("attainmentLevel.configure")}
      shouldCloseOnDocumentClick
    >
      {getModalHeader()}
      <Modal.Body>{getModalBody()}</Modal.Body>
      {getModalFooter()}
    </Modal>
  );
};
